import { ReactElement, useState, useCallback, useMemo, useEffect } from 'react';
import {
  DialogTitle,
  DialogContentText,
  Link,
  Typography,
  Button,
  Avatar,
  Stack,
  createFilterOptions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import { find } from 'lodash';

import { useAppSelector } from '~hooks/useAppSelector';
import { useTypePartnerCompany } from '~hooks/useTypePartnerCompany';
import { useSampleTemplates } from '~hooks/useSampleTemplates';
import { useImporterOrdersProgressListener } from '~hooks/useImporterOrdersProgressListener';
import { useLazyRequest } from '~hooks/useLazyRequest';
import { selectAuth } from '~store/slices/auth/index';
import { importItemsFromCSV } from '~services/item';
import { ItemsImporter } from '~services/item/types';
import {
  getImporterOrderFromCSVAlertDescription,
  isMercadoLibreImport,
  hasGeoImportErrors,
  getImportedOrderFromCSVErrorAlert,
} from '~utils/order';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import { hasError } from '~utils/formHelpers';
import FileUploadV2 from '~components/FileUploadV2';
import CustomAutocomplete from '~components/CustomAutocomplete';
import UserInfo from '~components/UserInfo';
import ImporterProgress from '~components/ImporterProgress';

import { useDialogImportOrdersFromCSVContext } from '../Context';
import ImportErrorsDetail from '../ImportErrorsDetail';
import {
  DialogImportOrdersFromCSVContentContainer,
  DialogImportOrdersFromCSVActions,
  DialogImportOrdersFromCSVNewsAlert,
} from '../styles';

import { FIELDS_NAME, getInitialValues, validationSchema } from './utils';

const DialogImportOrdersFromCSVContent = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAppSelector(selectAuth);
  const { isCarrier } = useTypePartnerCompany();

  const {
    onClose,
    onSubmitSuccess,
    onUpdateErrors,
    showAssociatedCompanies,
    associatedCompanies: { list: associatedCompanies },
  } = useDialogImportOrdersFromCSVContext();

  const { items: dowloadTemplateUrl } = useSampleTemplates();

  const [runImporterProgressListener, setImporterProgressListener] =
    useState(false);
  const [isCompletedRequest, setIsCompletedRequest] = useState(false);
  const [importedItemsData, setImportedItemsData] = useState<ItemsImporter>({
    errorRows: 0,
    importedRows: 0,
    message: '',
    totalRows: 0,
    errors: [],
    omittedErrors: 0,
    importedItems: 0,
    errorItems: 0,
  });

  const {
    status,
    updateImporterStatus,
    data: { progress, step, total },
  } = useImporterOrdersProgressListener({
    runListenerUpdates: runImporterProgressListener,
    onCompleted: () => {
      setImporterProgressListener(false);
    },
  });

  const loading = useMemo(() => status === 'inProgress', [status]);

  const isImporting = useMemo(
    () => loading || status === 'completed' || runImporterProgressListener,
    [loading, status, runImporterProgressListener],
  );

  const isCompletedProcessImporter = useMemo(
    () => status === 'completed' && isCompletedRequest,
    [status, isCompletedRequest],
  );

  const importerProgressTitle = useMemo(
    () =>
      isCompletedProcessImporter
        ? 'Importación finalizada'
        : 'Analizando archivo',
    [isCompletedProcessImporter],
  );

  const importerProgressDescription = useMemo(
    () =>
      isCompletedProcessImporter
        ? getImporterOrderFromCSVAlertDescription(importedItemsData)
        : `Analizando fila ${step} de ${total}`,
    [isCompletedProcessImporter, importedItemsData, step, total],
  );

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null;

    if (isCompletedProcessImporter && importedItemsData.errorRows < 1) {
      timeout = setTimeout(() => {
        onClose();
      }, 1500);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [importedItemsData.errorRows, isCompletedProcessImporter, onClose]);

  const [, , , executeImportItems] = useLazyRequest({
    request: importItemsFromCSV,
    withPostSuccess: (response) => {
      const responseImporter = response.data?.data as ItemsImporter;

      setImportedItemsData(responseImporter);

      setIsCompletedRequest(true);
      onSubmitSuccess();
    },
    withPostFailure: () => {
      setIsCompletedRequest(true);
      enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const onSubmit = useCallback(
    async (
      values: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      setImporterProgressListener(true);
      updateImporterStatus('inProgress');
      setIsCompletedRequest(false);

      await executeImportItems({
        file: values[FIELDS_NAME.FILES][0],
        ownerCompanyId: values[FIELDS_NAME.OWNER_COMPANY_ID] ?? null,
      });

      setSubmitting(false);
    },
    [updateImporterStatus, executeImportItems],
  );

  const formikBag = useFormik({
    initialValues: getInitialValues(user?.companyId ?? null),
    validationSchema,
    onSubmit,
  });

  const {
    submitForm,
    isSubmitting,
    dirty,
    isValid,
    values,
    setFieldValue,
    setFieldTouched,
    touched,
    errors,
  } = formikBag;

  const defaultSelectedOwnerCompany = useMemo(
    () =>
      user?.companyId
        ? find(associatedCompanies, { id: user.companyId })
        : undefined,
    [user?.companyId, associatedCompanies],
  );

  const disabledSubmit = useMemo(
    () => !dirty || !isValid || !values[FIELDS_NAME.FILES],
    [dirty, isValid, values],
  );

  const [showErrorsDetail, setShowErrorsDetail] = useState(false);

  const hasImportErrors = useMemo(
    () => isCompletedProcessImporter && importedItemsData.errorRows >= 1,
    [isCompletedProcessImporter, importedItemsData.errorRows],
  );

  const hasGeoErrors = useMemo(
    () => hasGeoImportErrors(importedItemsData.errors),
    [importedItemsData.errors],
  );

  const isMercadoLibre = useMemo(
    () => isMercadoLibreImport(importedItemsData),
    [importedItemsData],
  );

  const toggleErrorsDetail = useCallback(
    () => setShowErrorsDetail((prev) => !prev),
    [],
  );

  return (
    <FormikProvider value={formikBag}>
      <DialogTitle>Importar Pedidos</DialogTitle>

      <DialogImportOrdersFromCSVContentContainer dividers>
        {!isImporting && (
          <>
            <DialogContentText color="text.primary">
              Importar pedidos desde un archivo de forma masiva
            </DialogContentText>

            <DialogImportOrdersFromCSVNewsAlert>
              Ya podés subir el archivo de ventas de Mercado Libre tal como lo
              descargás, sin adaptarlo la plantilla.
            </DialogImportOrdersFromCSVNewsAlert>

            {showAssociatedCompanies && (
              <CustomAutocomplete
                fullWidth
                options={associatedCompanies}
                defaultValue={defaultSelectedOwnerCompany}
                disableClearable
                label="Seleccione la empresa"
                name="associatedCompanies"
                onChange={(_e, val) => {
                  setFieldValue(FIELDS_NAME.OWNER_COMPANY_ID, val.id);
                }}
                onBlur={() => {
                  setFieldTouched(FIELDS_NAME.OWNER_COMPANY_ID, true);
                }}
                error={hasError(touched, errors, FIELDS_NAME.OWNER_COMPANY_ID)}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <UserInfo
                      name={option.name}
                      urlAvatar={option.logoFile?.url}
                    />
                  </li>
                )}
                startAdornment={(val) =>
                  val && (
                    <Avatar
                      src={val.logoFile?.url}
                      alt={val?.name}
                      sx={{ width: 20, height: 20 }}
                    />
                  )
                }
                groupBy={(option) => {
                  if (option.id === user?.companyId) {
                    return 'Mi Compañia';
                  }

                  return 'Dador de Carga';
                }}
                filterOptions={createFilterOptions({
                  ignoreAccents: true,
                  ignoreCase: true,
                  stringify: (option) => option.name.replace(',', ''),
                })}
              />
            )}
          </>
        )}

        <FileUploadV2
          name={FIELDS_NAME.FILES}
          labelImg={{
            src: toAbsoluteUrl('/images/import_file.png'),
            alt: 'Import File',
          }}
          accept=".csv, .xls, .xlsx"
          disabled={isImporting}
        />

        {!isImporting && (
          <DialogContentText variant="caption">
            Si deseas importar de forma masiva utilizando un archivo, puedes{' '}
            <Link
              href={dowloadTemplateUrl}
              download
              color="primary"
              underline="hover"
              fontWeight="bold"
            >
              descargar y modificar la plantilla de ejemplo.
            </Link>{' '}
            <Typography
              variant="inherit"
              component="span"
              // @NEW-GIVER-DASHBOARD: Hide until the help URL is available for Givers
              visibility={isCarrier ? 'visible' : 'hidden'}
            >
              Si necesitas información consulta la{' '}
              <Link
                href="https://routix.tawk.help/article/cargar-pedidos-de-forma-masiva"
                download
                color="primary"
                underline="hover"
                fontWeight="bold"
              >
                ayuda.
              </Link>{' '}
            </Typography>
          </DialogContentText>
        )}

        {isImporting && (
          <ImporterProgress
            title={importerProgressTitle}
            description={importerProgressDescription}
            progress={progress}
            warning={importedItemsData.errorRows >= 1}
          />
        )}

        {hasImportErrors && (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Typography variant="body2" fontWeight={600}>
                {getImportedOrderFromCSVErrorAlert({
                  errorRows: importedItemsData.errorRows,
                  errorItems: importedItemsData.errorItems,
                })}
              </Typography>

              <Button size="small" onClick={toggleErrorsDetail}>
                {showErrorsDetail ? 'Ocultar detalle' : 'Ver detalle'}
              </Button>
            </Stack>

            {showErrorsDetail && (
              <ImportErrorsDetail
                errors={importedItemsData.errors}
                omittedErrors={importedItemsData.omittedErrors}
                isMercadoLibre={isMercadoLibre}
              />
            )}
          </>
        )}
      </DialogImportOrdersFromCSVContentContainer>

      <DialogImportOrdersFromCSVActions>
        <Button color="secondary" onClick={onClose}>
          Cerrar
        </Button>

        {!isImporting && (
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={submitForm}
            loading={loading || isSubmitting}
            disabled={disabledSubmit}
          >
            Importar
          </LoadingButton>
        )}

        {hasImportErrors && hasGeoErrors && (
          <Button variant="contained" color="primary" onClick={onUpdateErrors}>
            Corregir problemas de geolocalización
          </Button>
        )}
      </DialogImportOrdersFromCSVActions>
    </FormikProvider>
  );
};

export default DialogImportOrdersFromCSVContent;
